// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/i18n';
import { cn } from '../../utils/cn';
import EllipseTooltip from '../../utils/EllipseTooltip';

const DROPDOWN_GAP_PX = 6;
const DROPDOWN_MAX_HEIGHT_PX = 280;
const PLACEHOLDER_TEXT_CLASS = 'text-[#8a8c95] dark:text-[#90a1b9]';

function getDropdownPlacement(trigger, alignRight) {
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP_PX;
  const spaceAbove = rect.top - DROPDOWN_GAP_PX;
  const opensUpward = spaceBelow < DROPDOWN_MAX_HEIGHT_PX && spaceAbove > spaceBelow;
  const maxHeight = Math.min(DROPDOWN_MAX_HEIGHT_PX, opensUpward ? spaceAbove : spaceBelow);

  return {
    opensUpward,
    style: {
      position: 'fixed',
      left: alignRight ? undefined : rect.left,
      right: alignRight ? window.innerWidth - rect.right : undefined,
      top: opensUpward ? undefined : rect.bottom + DROPDOWN_GAP_PX,
      bottom: opensUpward ? window.innerHeight - rect.top + DROPDOWN_GAP_PX : undefined,
      width: rect.width,
      maxHeight,
      zIndex: 1001,
    },
  };
}

export default function Select({
  prefix,
  placeholder,
  value,
  options,
  selectedValue,
  onChange,
  dropdownClassName = '',
  alignRight = false,
  disabled = false,
  className = '',
  isFirstOptionPlaceholder = false,
  hasDeselectOption = false,
  loading = false,
  loadingText = '',
  triggerLabelClassName = '',
  ...rest
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [placement, setPlacement] = React.useState(null);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const [hoveredIndex, setHoveredIndex] = React.useState(-1);
  const rootRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const scrollableRef = React.useRef(null);
  const wasOpenRef = React.useRef(false);
  const shouldScrollToFocusRef = React.useRef(false);
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);

  const reposition = React.useCallback(() => {
    if (rootRef.current) {
      setPlacement(getDropdownPlacement(rootRef.current, alignRight));
    }
  }, [alignRight]);

  const isEmptyValue = value === '' || value == null;
  const scrollableOptions = options ?? [];
  const pinnedDeselectOption = hasDeselectOption && !isEmptyValue ? { value: '', label: t('common_deselect') } : null;
  const visibleOptions = pinnedDeselectOption ? [pinnedDeselectOption, ...scrollableOptions] : scrollableOptions;
  const selectedOptionLabel = selectedValue ?? scrollableOptions.find((opt) => opt.value === value)?.label ?? '';
  const triggerLabel =
    loading && loadingText ? loadingText : isEmptyValue && placeholder ? placeholder : selectedOptionLabel;
  const isPlaceholderState = loading || (isEmptyValue && Boolean(placeholder));

  React.useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      setFocusedIndex(-1);
      setHoveredIndex(-1);
      wasOpenRef.current = false;
      return;
    }
    reposition();
    if (!wasOpenRef.current) {
      const optionsWithDeselect =
        hasDeselectOption && !isEmptyValue ? [{ value: '', label: '' }, ...(options ?? [])] : (options ?? []);
      const currentIndex = optionsWithDeselect.findIndex((opt) => opt.value === value);
      setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
      shouldScrollToFocusRef.current = true;
      wasOpenRef.current = true;
    }
  }, [isOpen, options, reposition, value, hasDeselectOption, isEmptyValue]);

  React.useEffect(() => {
    if (!isOpen || focusedIndex < 0 || !shouldScrollToFocusRef.current) return;
    shouldScrollToFocusRef.current = false;
    if (pinnedDeselectOption && focusedIndex === 0) return;
    const scrollIndex = pinnedDeselectOption ? focusedIndex - 1 : focusedIndex;
    scrollableRef.current?.children[scrollIndex]?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, focusedIndex, pinnedDeselectOption]);

  React.useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      const target = event.target;
      if (rootRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    }

    function handleScroll(event) {
      if (dropdownRef.current?.contains(event.target)) return;
      reposition();
    }

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, reposition]);

  const renderOption = (opt, index, isDeselectItem) => {
    const isActive = opt.value === value && !isEmptyValue;
    const isFocused = index === focusedIndex || index === hoveredIndex;
    const isPlaceholderItem = isFirstOptionPlaceholder && index === 0 && !isDeselectItem;

    return (
      <div
        key={isDeselectItem ? 'deselect' : opt.value}
        role='option'
        aria-selected={isActive}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-lg p-2 text-base font-medium cursor-pointer',
          isDeselectItem
            ? 'text-[#2563EB] dark:text-[#3D8DD4]'
            : isPlaceholderItem
              ? 'text-[#A2A3AB] dark:text-[#90A1B9]'
              : 'text-[#45556c] dark:text-[#fafafa]',
          isFocused ? 'bg-[#eef1f6] dark:bg-[#1E293B]' : 'hover:bg-[#eef1f6] dark:hover:bg-[#1E293B]',
        )}
        onMouseEnter={() => setHoveredIndex(index)}
        onClick={() => {
          onChange?.(opt.value);
          setIsOpen(false);
        }}
      >
        <EllipseTooltip title={opt.label} className='min-w-0 flex-1 text-left leading-normal'>
          {opt.label}
        </EllipseTooltip>
        <span className='flex size-5 shrink-0 items-center justify-center' aria-hidden={!isActive}>
          {isActive ? <Check className='size-5 text-green-500' aria-hidden='true' /> : null}
        </span>
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative inline-flex items-center rounded-xl p-0 min-h-[44px] text-base',
        'bg-white border border-[#e1e5eb] text-[#2d3041]',
        'dark:bg-[#0c121d] dark:border-[#3c3e53] dark:text-[#fafafa]',
        'hover:border-[#cfd6df] dark:hover:border-[#4a4d66]',
        'focus-within:outline focus-within:outline-2 focus-within:outline-offset-0',
        'focus-within:outline-[rgba(37,99,235,0.7)] dark:focus-within:outline-[rgba(39,115,183,0.75)]',
        disabled && 'opacity-60 pointer-events-none',
        className,
      )}
    >
      <button
        type='button'
        className='inline-flex items-center gap-2.5 w-full min-h-[44px] border-0 bg-transparent py-[10px] px-3 rounded-xl text-base text-inherit cursor-pointer min-w-0 hover:!bg-transparent active:!bg-transparent focus:outline-none'
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
              return;
            }
            shouldScrollToFocusRef.current = true;
            setHoveredIndex(-1);
            setFocusedIndex((prev) => {
              if (e.key === 'ArrowDown') return Math.min(prev + 1, visibleOptions.length - 1);
              return Math.max(prev - 1, 0);
            });
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else if (focusedIndex >= 0 && visibleOptions[focusedIndex]) {
              onChange?.(visibleOptions[focusedIndex].value);
              setIsOpen(false);
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
          }
        }}
        {...rest}
      >
        {prefix ? (
          <span className='inline-flex items-center opacity-75 whitespace-nowrap text-base'>{prefix}</span>
        ) : null}
        <EllipseTooltip
          title={triggerLabel}
          showTooltip={!isPlaceholderState}
          className={cn(
            'flex-1 text-base leading-5 min-w-0 text-left',
            isPlaceholderState && PLACEHOLDER_TEXT_CLASS,
            triggerLabelClassName,
          )}
        >
          {triggerLabel}
        </EllipseTooltip>
        <ChevronDown
          className={cn('shrink-0 opacity-70 transition-transform', isOpen && placement?.opensUpward && 'rotate-180')}
          size={18}
        />
      </button>

      {isOpen &&
        placement &&
        createPortal(
          <div
            ref={dropdownRef}
            role='listbox'
            style={placement.style}
            onMouseLeave={() => setHoveredIndex(-1)}
            className={cn(
              'flex min-h-0 flex-col min-w-0 overflow-hidden',
              'rounded-lg',
              'bg-white border border-[#e1e5eb]',
              'dark:bg-[#0C121D] dark:border dark:border-[#3C3E53]',
              'shadow-[0px_8px_16px_0px_rgba(0,0,0,0.12)] dark:shadow-[0px_8px_24px_0px_rgba(0,0,0,0.5)]',
              dropdownClassName,
            )}
          >
            {pinnedDeselectOption ? (
              <div className='shrink-0 border-b border-[#e1e5eb] bg-white px-2 pt-2 pb-2 dark:border-[#3C3E53] dark:bg-[#0C121D]'>
                {renderOption(pinnedDeselectOption, 0, true)}
              </div>
            ) : null}
            <div
              ref={scrollableRef}
              className={cn(
                'flex min-h-0 flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto p-2',
                '[scrollbar-width:thin] [scrollbar-color:rgba(148,163,184,0.55)_transparent]',
                '[&::-webkit-scrollbar]:w-[10px]',
                '[&::-webkit-scrollbar-track]:bg-transparent',
                '[&::-webkit-scrollbar-thumb]:bg-[rgba(148,163,184,0.45)] [&::-webkit-scrollbar-thumb]:rounded-full',
                '[&::-webkit-scrollbar-thumb]:[border:2px_solid_transparent] [&::-webkit-scrollbar-thumb]:[background-clip:padding-box]',
              )}
            >
              {scrollableOptions.map((opt, index) =>
                renderOption(opt, pinnedDeselectOption ? index + 1 : index, false),
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
